import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Input, Select, Space, Statistic, Table, Tabs, Tag, Typography, message
} from 'antd';
import { LogoutOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { csDeskAPI } from '../services/api';
import { clearAuthStorage, getStoredUser } from '../utils/auth';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const DATE_FORMAT = 'YYYY-MM-DD';

function formatAmount(value) {
  return Number(value || 0).toFixed(2);
}

export default function CsDesk() {
  const navigate = useNavigate();
  const storedUser = getStoredUser() || {};
  const [scope, setScope] = useState('all');
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState();
  const [channels, setChannels] = useState([]);
  const [channelKey, setChannelKey] = useState();
  const [keyword, setKeyword] = useState('');
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, totalAmount: 0, agentCount: 0, operatorCount: 0 });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const loadAgents = useCallback(async () => {
    try {
      const [agentRes, channelRes] = await Promise.all([
        csDeskAPI.getAgents(),
        csDeskAPI.getChannels()
      ]);
      setAgents(agentRes.data?.data?.list || []);
      setChannels(channelRes.data?.data?.list || []);
    } catch (error) {
      message.error(error.response?.data?.message || '加载名下代理失败');
    }
  }, []);

  const loadLedgers = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const [start, end] = dateRange || [];
      const response = await csDeskAPI.getLedgers({
        scope,
        ledger_type: undefined,
        channel_key: scope === 'all' ? channelKey : undefined,
        agent_id: scope === 'downline' ? agentId : undefined,
        keyword,
        date_start: start ? start.format(DATE_FORMAT) : undefined,
        date_end: end ? end.format(DATE_FORMAT) : undefined,
        page,
        limit: pageSize
      });
      const data = response.data?.data || {};
      setRows(data.list || []);
      setSummary(data.summary || { total: 0, totalAmount: 0, agentCount: 0, operatorCount: 0 });
      setPagination({
        current: data.pagination?.page || page,
        pageSize: data.pagination?.limit || pageSize,
        total: data.pagination?.total || 0
      });
    } catch (error) {
      message.error(error.response?.data?.message || '加载流水失败');
    } finally {
      setLoading(false);
    }
  }, [agentId, channelKey, dateRange, keyword, scope]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    loadLedgers(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, channelKey, agentId]);

  const columns = useMemo(() => [
        {
          title: '时间',
          dataIndex: 'payment_time',
          width: 170,
          render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-')
        },
        {
          title: '玩家账号',
          dataIndex: 'user_name',
          width: 140,
          render: (_, row) => row.user_name || row.nick_name || '-'
        },
        {
          title: '渠道',
          dataIndex: 'channel_key',
          width: 160,
          render: (value, row) => value || row.channelKey || '-'
        },
        {
          title: '操作代理',
          dataIndex: 'operator_name',
          width: 160,
          render: (value) => <Text strong>{value || '-'}</Text>
        },
        {
          title: '操作前余额',
          dataIndex: 'agent_balance_before',
          width: 130,
          align: 'right',
          render: (value) => (value === null || value === undefined ? '-' : `¥${formatAmount(value)}`)
        },
        {
          title: '操作后余额',
          dataIndex: 'agent_balance_after',
          width: 130,
          align: 'right',
          render: (value) => (value === null || value === undefined ? '-' : `¥${formatAmount(value)}`)
        },
        {
          title: '充值金额',
          dataIndex: 'total_amount',
          width: 110,
          align: 'right',
          render: (value) => `¥${formatAmount(value)}`
        },
        {
          title: '订单号',
          dataIndex: 'order_no',
          width: 220,
          render: (value, row) => value || row.order_id || row.id || '-'
        },
        {
          title: '备注',
          dataIndex: 'remark',
          ellipsis: true,
          render: (value) => value || '-'
        }
  ], []);

  const onLogout = () => {
    clearAuthStorage();
    navigate('/cs/login', { replace: true });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fb', padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 1440, margin: 'auto' }}>
        <Card>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>人工客服后台</Title>
              <Text type="secondary">
                {storedUser.agentName || storedUser.username || '客服'} · 查人工充值 / 名下代理流水
              </Text>
            </div>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => loadLedgers(pagination.current, pagination.pageSize)}>刷新</Button>
              <Button icon={<LogoutOutlined />} onClick={onLogout}>退出</Button>
            </Space>
          </Space>
        </Card>

        <Alert
          type="info"
          showIcon
          message={scope === 'all'
            ? '全部流水只查人工代充渠道（含补单），不含 PayPal、Visa。渠道与代理后台「人工代充」订单列表同一列。'
            : '名下代理流水只查你和你下级客服操作过的人工代充（含补单），按操作人筛选。'}
        />

        <Card>
          <Tabs
            activeKey={scope}
            onChange={(key) => {
              setScope(key);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            items={[
              { key: 'all', label: '全部流水' },
              { key: 'downline', label: '名下代理流水' }
            ]}
          />
          <Space wrap style={{ marginBottom: 16 }}>
            <RangePicker
              value={dateRange}
              format={DATE_FORMAT}
              allowClear={false}
              onChange={(value) => setDateRange(value)}
            />
            {scope === 'all' ? (
              <Select
                allowClear
                placeholder="全部渠道"
                value={channelKey}
                style={{ minWidth: 220 }}
                onChange={(value) => {
                  setChannelKey(value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                options={channels.map((item) => ({
                  value: item.channelKey,
                  label: item.name ? `${item.name}（${item.channelKey}）` : item.channelKey
                }))}
              />
            ) : null}
            {scope === 'downline' ? (
              <Select
                allowClear
                showSearch
                placeholder="全部名下代理"
                style={{ minWidth: 220 }}
                value={agentId}
                optionFilterProp="label"
                onChange={setAgentId}
                options={agents.map((item) => ({
                  value: item.id,
                  label: `${item.agentName}${item.isSelf ? '（自己）' : ''} · ${item.agentRoleName || ''}`
                }))}
              />
            ) : null}
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={scope === 'all' ? '玩家 / 订单号 / 操作人' : '玩家 / 订单号'}
              style={{ width: 240 }}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onPressEnter={() => loadLedgers(1, pagination.pageSize)}
            />
            <Button type="primary" onClick={() => loadLedgers(1, pagination.pageSize)}>查询</Button>
          </Space>
          <Space size={32} style={{ marginBottom: 16 }}>
            <Statistic title="笔数" value={summary.total || 0} />
            <Statistic title="合计金额" prefix="¥" precision={2} value={Number(summary.totalAmount || 0)} />
            {scope === 'all'
              ? <Statistic title="操作人数" value={summary.operatorCount || summary.agentCount || 0} />
              : <Statistic title="覆盖代理" value={summary.agentCount || 0} />}
          </Space>
          {scope === 'all' ? <Tag color="red">人工代充</Tag> : <Tag color="blue">仅名下</Tag>}
          <Table
            style={{ marginTop: 12 }}
            rowKey={(row) => row.row_key || `${row.id || row.order_no}-${row.payment_time || ''}`}
            loading={loading}
            columns={columns}
            scroll={{ x: 1200 }}
            dataSource={rows}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 笔`,
              onChange: (page, pageSize) => loadLedgers(page, pageSize)
            }}
          />
        </Card>
      </Space>
    </div>
  );
}
